import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Customer, Staff } from '@/lib/types';
import { useAuth } from '@/store/auth';
import {
  Avatar,
  Banner,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';
import { CustomerHoldingsModal } from '@/components/CustomerHoldingsModal';

export function CustomersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', tags: '', source: 'walk_in' });
  const [reassign, setReassign] = useState<Customer | null>(null);
  const [detail, setDetail] = useState<Customer | null>(null);
  const [reassignTo, setReassignTo] = useState({ newOwnerStaffId: '', reason: '' });

  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });

  const staffName = (id: string | null) => staff.data?.find((s) => s.id === id)?.name ?? id ?? '';

  const create = useMutation({
    mutationFn: () =>
      api.post('/customers', {
        name: form.name,
        phone: form.phone,
        tags: form.tags ? form.tags.split(/[,，\s]+/).filter(Boolean) : [],
      }),
    onSuccess: () => {
      ok('顧客已建立');
      setAddOpen(false);
      setForm({ name: '', phone: '', tags: '', source: 'walk_in' });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: fail,
  });

  const doReassign = useMutation({
    mutationFn: () => api.post(`/customers/${reassign!.id}/reassign`, reassignTo),
    onSuccess: () => {
      ok('主責已移轉並寫入稽核軌跡');
      setReassign(null);
      setReassignTo({ newOwnerStaffId: '', reason: '' });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: fail,
  });

  const rows = useMemo(() => {
    const list = customers.data ?? [];
    if (!q) return list;
    return list.filter((c) => c.name.includes(q) || c.phone.includes(q));
  }, [customers.data, q]);

  const sort = useSort(rows, {
    name: (c) => c.name,
    phone: (c) => c.phone,
    tier: (c) => c.tier,
    tags: (c) => (c.tags ?? []).join(','),
    owner: (c) => staffName(c.ownerStaffId),
  });

  const mine = (customers.data ?? []).filter((c) => c.ownerStaffId === user?.userId).length;
  const unassigned = (customers.data ?? []).filter((c) => !c.ownerStaffId).length;

  const isManagement = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const scopeNote = isManagement
    ? '主管視角：可見全租戶顧客'
    : '依歸屬可見：僅自己主責 + 未指派（防撈客）';

  return (
    <div>
      <PageHeader
        title="顧客主頁"
        subtitle={scopeNote}
        right={
          <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
            <Icon d={I.plus} size={13} /> 新增顧客
          </button>
        }
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="可見顧客" value={String(customers.data?.length ?? 0)} />
        <Stat label="我的主責" value={String(mine)} tone="primary" hint="業績歸屬鎖" />
        <Stat label="未指派" value={String(unassigned)} tone="warn" hint="可認領" />
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: 12, borderBottom: '1px solid var(--line-soft)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 10px',
              background: 'var(--surface-2)',
              border: '1px solid var(--line-soft)',
              borderRadius: 8,
              maxWidth: 320,
            }}
          >
            <Icon d={I.search} size={14} style={{ color: 'var(--ink-3)' }} />
            <input
              placeholder="搜尋姓名 / 電話"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 12 }}
            />
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="顧客" sortKey="name" sort={sort} />
              <SortTh label="電話" sortKey="phone" sort={sort} />
              <SortTh label="分級" sortKey="tier" sort={sort} />
              <SortTh label="標籤" sortKey="tags" sort={sort} />
              <SortTh label="主責歸屬" sortKey="owner" sort={sort} />
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((c) => (
              <tr key={c.id}>
                <td style={cellStyle}>
                  <button
                    onClick={() => setDetail(c)}
                    title="檢視儲值金 / 堂票"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                  >
                    <Avatar name={c.name} size={30} tone="auto" />
                    <span style={{ fontWeight: 600, color: 'var(--primary-deep, var(--ink))' }}>
                      {c.name}
                    </span>
                  </button>
                </td>
                <td style={cellStyle} className="num">
                  {c.phone}
                </td>
                <td style={cellStyle}>
                  <Tag tone="primary">{c.tier}</Tag>
                </td>
                <td style={cellStyle}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {c.tags.map((t) => (
                      <Tag key={t} tone="tag">
                        {t}
                      </Tag>
                    ))}
                  </div>
                </td>
                <td style={cellStyle}>
                  {c.ownerStaffId ? (
                    c.ownerStaffId === user?.userId ? (
                      <Tag tone="success" dot>
                        我的客戶
                      </Tag>
                    ) : (
                      <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{staffName(c.ownerStaffId)}</span>
                    )
                  ) : (
                    <Tag tone="default">未指派</Tag>
                  )}
                </td>
                <td style={cellStyle}>
                  {isManagement && (
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '4px 9px', fontSize: 11 }}
                      onClick={() => setReassign(c)}
                    >
                      移轉主責
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={6}>
                  無可見顧客
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} width={460}>
        <ModalHeader title="30 秒新客建立" sub="最少必填 2 欄 · 系統自動產生 UID 與業績歸屬鎖" onClose={() => setAddOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="姓名 *">
              <input style={inputStyle} value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="李心慈" />
            </Field>
            <Field label="電話 *">
              <input style={inputStyle} className="num" value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} placeholder="0912-345-678" />
            </Field>
          </div>
          <Field label="標籤（逗號分隔，可選）">
            <input style={inputStyle} value={form.tags} onChange={(e) => setForm((s) => ({ ...s, tags: e.target.value }))} placeholder="VIP, 雷射" />
          </Field>
          <div style={{ padding: 10, background: 'var(--surface-2)', borderRadius: 6, fontSize: 10.5, color: 'var(--ink-2)' }}>
            <b>✓ 系統自動處理：</b>
            <span style={{ color: 'var(--ink-3)' }}> 產生新 UID · 主責歸屬 → {user?.name}（業績鎖）· 建立 LINE 待綁定連結</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setAddOpen(false)}>
            取消
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1.5 }}
            disabled={!form.name || !form.phone || create.isPending}
            onClick={() => create.mutate()}
          >
            <Icon d={I.check} size={13} /> 建立顧客
          </button>
        </div>
      </Modal>

      <Modal open={!!reassign} onClose={() => setReassign(null)} width={440}>
        <ModalHeader title="移轉主責歸屬" sub={`${reassign?.name} · 寫入防撈客稽核軌跡`} onClose={() => setReassign(null)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="新主責人員">
            <Select
              value={reassignTo.newOwnerStaffId}
              onChange={(val) => setReassignTo((s) => ({ ...s, newOwnerStaffId: val }))}
              placeholder="— 選擇人員 —"
              options={(staff.data ?? []).map((s) => ({ value: s.id, label: `${s.name}（${s.role}）` }))}
            />
          </Field>
          <Field label="移轉原因 *">
            <input style={inputStyle} value={reassignTo.reason} onChange={(e) => setReassignTo((s) => ({ ...s, reason: e.target.value }))} placeholder="例：原諮詢師離職交接" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setReassign(null)}>
            取消
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1.5 }}
            disabled={!reassignTo.newOwnerStaffId || !reassignTo.reason || doReassign.isPending}
            onClick={() => doReassign.mutate()}
          >
            確認移轉
          </button>
        </div>
      </Modal>

      <CustomerHoldingsModal customer={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
